import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Plus, Trash2, GraduationCap } from "lucide-react";
import api, { apiErr } from "@/lib/api";
import { fmtDate } from "@/lib/format";
import { CARD, Label, EmptyState } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { BTN, TRAINING_STATUS } from "./hrUtils";

const EMPTY = { employee_id: "", title: "", organisme: "", date_debut: "", date_fin: "", heures: "", cout: "", statut: "prevue", expiry_date: "" };

export const HrTrainings = ({ emps, ename, onChanged }) => {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [f, setF] = useState(EMPTY);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const load = useCallback(() => api.get("/hr/trainings").then((r) => setRows(r.data)).catch((e) => toast.error(apiErr(e))), []);
  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    if (!f.employee_id || !f.title) { toast.error("Salarié et intitulé obligatoires"); return; }
    try { await api.post("/hr/trainings", f); toast.success("Formation enregistrée"); setOpen(false); setF(EMPTY); load(); onChanged && onChanged(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const setStatus = async (id, statut) => { try { await api.put(`/hr/trainings/${id}`, { statut }); load(); } catch (e) { toast.error(apiErr(e)); } };
  const del = async (id) => { if (!window.confirm("Supprimer cette formation ?")) return; try { await api.delete(`/hr/trainings/${id}`); load(); onChanged && onChanged(); } catch (e) { toast.error(apiErr(e)); } };
  const totalH = rows.filter((r) => r.statut === "realisee").reduce((s, r) => s + (r.heures || 0), 0);

  return (
    <div className={`${CARD} p-4 sm:p-6`} data-testid="rh-trainings">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div><Label>Formations</Label><p className="text-xs text-gray-400">{totalH} h réalisées au total</p></div>
        <Button data-testid="rh-add-training" onClick={() => setOpen(true)} className={BTN}><Plus className="mr-1 h-4 w-4" />Nouvelle formation</Button>
      </div>
      {rows.length === 0 ? <EmptyState title="Aucune formation" subtitle="Enregistrez les formations suivies ou prévues." /> : (
        <div className="divide-y divide-gray-50">
          {rows.map((r) => (
            <div key={r.id} className="flex flex-wrap items-center gap-3 py-3" data-testid={`rh-training-${r.id}`}>
              <GraduationCap className="h-4 w-4 text-[#065F46]" />
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-semibold">{ename(r.employee_id)} · {r.title}</p>
                <p className="text-xs text-gray-400">{r.organisme || "—"} · {r.date_debut ? fmtDate(r.date_debut) : "date N/A"}{r.date_fin ? ` → ${fmtDate(r.date_fin)}` : ""} · {r.heures || 0} h{r.cout ? ` · ${r.cout} €` : ""}{r.expiry_date ? ` · recyclage ${fmtDate(r.expiry_date)}` : ""}</p>
              </div>
              <Select value={r.statut} onValueChange={(v) => setStatus(r.id, v)}>
                <SelectTrigger className="h-8 w-32 rounded-full text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(TRAINING_STATUS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
              </Select>
              <button onClick={() => del(r.id)} className="rounded-lg p-1.5 text-rose-500 hover:bg-rose-50"><Trash2 className="h-4 w-4" /></button>
            </div>
          ))}
        </div>
      )}
      <Dialog open={open} onOpenChange={setOpen}><DialogContent className="max-h-[90vh] overflow-y-auto rounded-3xl sm:max-w-md" data-testid="rh-training-dialog">
        <DialogHeader><DialogTitle className="font-display text-xl">Nouvelle formation</DialogTitle></DialogHeader>
        <div className="space-y-3 pt-2">
          <div><Label>Salarié *</Label><Select value={f.employee_id || undefined} onValueChange={(v) => set("employee_id", v)}><SelectTrigger data-testid="rh-training-emp" className="mt-1.5 rounded-xl"><SelectValue placeholder="Choisir" /></SelectTrigger><SelectContent>{emps.map((e) => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}</SelectContent></Select></div>
          <div><Label>Intitulé *</Label><Input data-testid="rh-training-title" value={f.title} onChange={(e) => set("title", e.target.value)} className="mt-1.5 rounded-xl" /></div>
          <div><Label>Organisme</Label><Input value={f.organisme} onChange={(e) => set("organisme", e.target.value)} className="mt-1.5 rounded-xl" /></div>
          <div className="grid grid-cols-2 gap-2">
            <div><Label>Début</Label><Input data-testid="rh-training-start" type="date" value={f.date_debut} onChange={(e) => set("date_debut", e.target.value)} className="mt-1.5 rounded-xl" /></div>
            <div><Label>Fin</Label><Input type="date" value={f.date_fin} onChange={(e) => set("date_fin", e.target.value)} className="mt-1.5 rounded-xl" /></div>
            <div><Label>Heures</Label><Input type="number" value={f.heures} onChange={(e) => set("heures", e.target.value)} className="mt-1.5 rounded-xl" /></div>
            <div><Label>Coût (€)</Label><Input type="number" value={f.cout} onChange={(e) => set("cout", e.target.value)} className="mt-1.5 rounded-xl" /></div>
          </div>
          <div><Label>Date de recyclage / expiration</Label><Input data-testid="rh-training-expiry" type="date" value={f.expiry_date} onChange={(e) => set("expiry_date", e.target.value)} className="mt-1.5 rounded-xl" /></div>
          <Button data-testid="rh-training-submit" onClick={submit} className={`w-full py-6 ${BTN}`}>Enregistrer</Button>
        </div>
      </DialogContent></Dialog>
    </div>
  );
};
